import { isFoodAmigosHost } from './hosts';

export const PARTNER_QUERY_PARAM = 'partner';
export const PARTNER_SLUG_STORAGE_KEY = 'WHITE_LABEL_PARTNER';

// Same shape the backend accepts on /brand-config?partner= (a DNS label).
const PARTNER_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const PARTNER_SLUG_MAX_LENGTH = 63;

export function normalizePartnerSlug(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const slug = value.trim().toLowerCase();

  if (!slug || slug.length > PARTNER_SLUG_MAX_LENGTH || !PARTNER_SLUG_PATTERN.test(slug)) {
    return null;
  }

  return slug;
}

export function readPartnerSlugFromLocation(search = window.location.search) {
  return normalizePartnerSlug(new URLSearchParams(search).get(PARTNER_QUERY_PARAM));
}

// sessionStorage: one partner per tab, gone when the tab closes. Every access is
// guarded — Safari private mode and sandboxed WebViews can throw on touch.
export function getPersistedPartnerSlug() {
  try {
    return normalizePartnerSlug(window.sessionStorage.getItem(PARTNER_SLUG_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function persistPartnerSlug(slug) {
  const normalized = normalizePartnerSlug(slug);

  if (!normalized) {
    return;
  }

  try {
    window.sessionStorage.setItem(PARTNER_SLUG_STORAGE_KEY, normalized);
  } catch {
    // Storage unavailable — the URL param still brands this page load.
  }
}

export function clearPersistedPartnerSlug() {
  try {
    window.sessionStorage.removeItem(PARTNER_SLUG_STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

// The URL wins (and is remembered for the follow-up logged-out screens); otherwise
// the slug persisted earlier in this tab.
export function resolvePartnerSlug(search = window.location.search) {
  const fromLocation = readPartnerSlugFromLocation(search);

  if (fromLocation) {
    persistPartnerSlug(fromLocation);

    return fromLocation;
  }

  return getPersistedPartnerSlug();
}

function partnerSearch() {
  const slug = getPersistedPartnerSlug();

  return slug ? `?${new URLSearchParams({ [PARTNER_QUERY_PARAM]: slug })}` : '';
}

// Router-shaped location for <Navigate to> / navigate().
export function partnerAwarePath(pathname) {
  return { pathname, search: partnerSearch() };
}

// String variant for window.location.replace() and plain hrefs.
export function partnerAwareUrl(pathname) {
  return `${pathname}${partnerSearch()}`;
}

// Called right before the session is torn down, so the login page the user lands on
// keeps the brand they just used. The master provider IS FoodAmigos → never a slug.
export function syncPartnerSlugForLogout(serviceProvider) {
  if (serviceProvider?.is_master) {
    clearPersistedPartnerSlug();

    return;
  }

  const slug = normalizePartnerSlug(serviceProvider?.dashboard_subdomain);

  if (slug) {
    persistPartnerSlug(slug);

    return;
  }

  // No slug known for this session: FoodAmigos hosts fall back to the FoodAmigos brand,
  // shared white-label hosts keep whatever slug this tab already had.
  if (isFoodAmigosHost(window.location.hostname)) {
    clearPersistedPartnerSlug();
  }
}
